import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import InsightButton from "../components/InsightButton";
import { confirm } from "../utils/confirm";
import { logEvento, TIPI_EVENTO } from "../utils/logEvento";
import { collection, query, where, onSnapshot, doc, setDoc, updateDoc, deleteDoc, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { ChevronLeft, ChevronRight, FileText, Send, Settings, Printer, Download } from "lucide-react";
import { usePeriodoLavorativo, giorniAttesiMese, giorniMancanti } from "../utils/periodoLavorativo";
import { logModifica } from "../utils/logModifica";
import { numIt } from "../utils/economiaCommessa";

const MESI = ["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"];
const GIORNI_SETT = ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"];

const STATI_NS = {
  bozza:       { label:"In compilazione", colore:"#6b7280", bg:"#f3f4f6" },
  inviata:     { label:"Inviata",         colore:"#2563eb", bg:"#dbeafe" },
  in_verifica: { label:"In verifica",     colore:"#d97706", bg:"#fef3c7" },
  approvata:   { label:"Approvata",       colore:"#059669", bg:"#d1fae5" },
  respinta:    { label:"Respinta",        colore:"#dc2626", bg:"#fee2e2" },
  liquidata:   { label:"Liquidata",       colore:"#7c3aed", bg:"#ede9fe" },
};

const STATI_PAG = {
  "-":          { label:"—" },
  da_incassare: { label:"Da incassare", colore:"#d97706" },
  incassata:    { label:"Incassata",    colore:"#059669" },
};

// Stessa lettura degli importi di tutta l'app: "1.234,50" → 1234,5 (prima diventava 1,234)
function parseN(s) { return numIt(s); }
function fmt(n) {
  const v = parseN(n);
  if (v === 0) return "";
  return v.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtTot(n) {
  return parseN(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function pad2(n){ return String(n).padStart(2,"0"); }
function calcolaPasqua(anno){
  const a = anno % 19, b = Math.floor(anno/100), c = anno % 100;
  const d = Math.floor(b/4), e = b % 4, f = Math.floor((b+8)/25);
  const g = Math.floor((b-f+1)/3), h = (19*a+b-d-g+15) % 30;
  const i = Math.floor(c/4), k = c % 4, l = (32+2*e+2*i-h-k) % 7;
  const m = Math.floor((a+11*h+22*l)/451);
  const mese = Math.floor((h+l-7*m+114)/31), giorno = ((h+l-7*m+114) % 31) + 1;
  return new Date(anno, mese-1, giorno);
}
function festivitaItaliane(anno){
  const pasqua = calcolaPasqua(anno);
  const pasquetta = new Date(pasqua); pasquetta.setDate(pasqua.getDate()+1);
  const fmt = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
  return new Set([
    `${anno}-01-01`, `${anno}-01-06`, fmt(pasqua), fmt(pasquetta),
    `${anno}-04-25`, `${anno}-05-01`, `${anno}-06-02`, `${anno}-08-15`,
    `${anno}-11-01`, `${anno}-12-08`, `${anno}-12-25`, `${anno}-12-26`,
  ]);
}
function giorniDelMese(anno, mese) {
  const n = new Date(anno, mese + 1, 0).getDate();
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(anno, mese, i + 1);
    return { giorno: i + 1, dow: GIORNI_SETT[d.getDay()], isSabDom: d.getDay() === 0 || d.getDay() === 6 };
  });
}

// ── Celle inline ──────────────────────────────────────────────────────────
function CellaNum({ value, onChange, disabled, id, attiva, onNav }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value || "");
  const inputRef = useRef(null);
  useEffect(() => { setLocal(value || ""); }, [value]);
  // Attivata da fuori (navigazione a frecce): si "sveglia" ed entra in modifica da sola
  useEffect(() => { if (attiva && !disabled) setEditing(true); }, [attiva]);
  useEffect(() => {
    if (editing && attiva && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing, attiva]);
  if (disabled) return <div style={{ padding:"3px 6px", fontSize:12, textAlign:"right", color:"#9ca3af" }}>{value ? fmt(value) : ""}</div>;
  const naviga = (direzione) => { setEditing(false); onChange(local); onNav && onNav(direzione); };
  if (editing) return (
    <input ref={inputRef} id={id} autoFocus type="number" step="0.01" min="0" value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { setEditing(false); onChange(local); }}
      onKeyDown={e => {
        if(e.key==="Enter"){setEditing(false);onChange(local);}
        else if(e.key==="Escape"){setEditing(false);setLocal(value||"");}
        else if(e.key==="ArrowUp"){e.preventDefault();naviga("up");}
        else if(e.key==="ArrowDown"){e.preventDefault();naviga("down");}
        else if(e.key==="ArrowLeft"){e.preventDefault();naviga("left");}
        else if(e.key==="ArrowRight"){e.preventDefault();naviga("right");}
      }}
      style={{ width:"100%", padding:"2px 4px", border:"1px solid #7c3aed", borderRadius:3, fontSize:12, textAlign:"right", fontFamily:"'Inter',sans-serif", outline:"none", background:"#fffbff" }}/>
  );
  return (
    <div onClick={() => setEditing(true)}
      style={{ padding:"3px 6px", fontSize:12, textAlign:"right", cursor:"pointer", borderRadius:3, color: parseN(value) > 0 ? "#111827" : "#d1d5db" }}
      title="Clicca per modificare">
      {parseN(value) > 0 ? fmt(value) : "—"}
    </div>
  );
}

function CellaTesto({ value, onChange, disabled, placeholder, id, attiva, onNav }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(value || "");
  const inputRef = useRef(null);
  useEffect(() => { setLocal(value || ""); }, [value]);
  useEffect(() => { if (attiva && !disabled) setEditing(true); }, [attiva]);
  useEffect(() => {
    if (editing && attiva && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [editing, attiva]);
  if (disabled) return <div style={{ padding:"3px 6px", fontSize:11, color:"#9ca3af" }}>{value || ""}</div>;
  const naviga = (direzione) => { setEditing(false); onChange(local); onNav && onNav(direzione); };
  if (editing) return (
    <input ref={inputRef} id={id} autoFocus type="text" value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => { setEditing(false); onChange(local); }}
      onKeyDown={e => {
        if(e.key==="Enter"){setEditing(false);onChange(local);}
        else if(e.key==="Escape"){setEditing(false);setLocal(value||"");}
        else if(e.key==="ArrowUp"){e.preventDefault();naviga("up");}
        else if(e.key==="ArrowDown"){e.preventDefault();naviga("down");}
        else if(e.key==="ArrowLeft"&&e.target.selectionStart===0){e.preventDefault();naviga("left");}
        else if(e.key==="ArrowRight"&&e.target.selectionStart===e.target.value.length){e.preventDefault();naviga("right");}
      }}
      style={{ width:"100%", padding:"2px 4px", border:"1px solid #7c3aed", borderRadius:3, fontSize:11, fontFamily:"'Inter',sans-serif", outline:"none" }}/>
  );
  return (
    <div onClick={() => setEditing(true)}
      style={{ padding:"3px 6px", fontSize:11, cursor:"pointer", color: value ? "#111827" : "#d1d5db", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}
      title={value || placeholder}>
      {value || placeholder || "—"}
    </div>
  );
}

function CellaCommessa({ value, onChange, commesse, disabled, id, attiva, onNav }) {
  const selectRef = useRef(null);
  useEffect(() => { if (attiva && selectRef.current) selectRef.current.focus(); }, [attiva]);
  if (disabled) {
    const c = commesse.find(x => x.id === value);
    return <div style={{ padding:"3px 6px", fontSize:10, color:"#6b7280" }}>{c ? `${c.codiceCommessa?`[${c.codiceCommessa}] `:""}${c.name}` : "—"}</div>;
  }
  return (
    <select ref={selectRef} id={id} value={value || ""} onChange={e => onChange(e.target.value)}
      onKeyDown={e => {
        if(e.key==="ArrowUp"){e.preventDefault();onNav&&onNav("up");}
        else if(e.key==="ArrowDown"){e.preventDefault();onNav&&onNav("down");}
        else if(e.key==="ArrowLeft"){e.preventDefault();onNav&&onNav("left");}
        else if(e.key==="ArrowRight"){e.preventDefault();onNav&&onNav("right");}
      }}
      style={{ width:"100%", padding:"2px 4px", border:"none", background:"transparent", fontSize:10, fontFamily:"'Inter',sans-serif", outline:"none", cursor:"pointer", color: value ? "#7c3aed" : "#9ca3af" }}>
      <option value="">— Generale —</option>
      {commesse.map(c => <option key={c.id} value={c.id}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.name}</option>)}
    </select>
  );
}

// ── Vista riepilogo annuale ───────────────────────────────────────────────
function RiepilogoAnnuale({ anno, uid, orgId, canVerify, onBack }) {
  const { addToast } = useToast();

  const [annoRiep, setAnnoRiep] = React.useState(anno);
  const [noteRiep, setNoteRiep] = React.useState([]);

  React.useEffect(()=>{
    if(!orgId||!uid) return;
    const q = query(collection(db,"noteSpese"),where("orgId","==",orgId));
    return onSnapshot(q, snap=>{
      const note = snap.docs.map(d=>({id:d.id,...d.data()})).filter(d=>String(d.uid)===String(uid)&&String(d.anno)===String(annoRiep));
      setNoteRiep(note);
      // Rete di sicurezza: se una nota risulta approvata/liquidata ma non ha ancora nessun
      // movimento economico associato (es. per un problema tecnico nel momento
      // dell'approvazione), lo crea automaticamente qui — stesso schema della vera pagina
      // di approvazione (notaSpeseRef), non quello vecchio ormai dismesso.
      note.forEach(async ns => {
        if(ns.stato !== "approvata" && ns.stato !== "liquidata") return;
        const righeNs = ns.righe || {};
        const righeValide = Object.entries(righeNs).filter(([,r])=>r.commessaId && numIt(r.totale||0)>0);
        if (righeValide.length===0) return;
        const movEsistentiSnap = await getDocs(query(collection(db,"movimentiEconomici"), where("notaSpeseRef","==",ns.id), where("organizationId","==",orgId)));
        if (!movEsistentiSnap.empty) return; // già presenti, nessuna riparazione necessaria
        for (const [giorno, riga] of righeValide) {
          // Stesso id e stesso importo (somma delle voci) della pagina di approvazione:
          // anche se entrambe scrivono, il movimento resta uno solo.
          await setDoc(doc(db,"movimentiEconomici",`ns_${ns.id}_${giorno}`), {
            projectId: riga.commessaId, organizationId: orgId, clienteId: null,
            tipo:"costo", categoriaId:"nota_spese", categoriaLabel:"Note Spese",
            importo: parseN(riga.rimborsoKm)+parseN(riga.pedaggi)+parseN(riga.parcheggi)+parseN(riga.barRistoranti)+parseN(riga.varie),
            dati:{ descrizione:`Nota spese — ${giorno}/${ns.mese}/${ns.anno}`, destinazione:riga.destinazione||"" },
            notaSpeseRef: ns.id, createdAt:serverTimestamp(),
          });
        }
      });
    });
  },[orgId,uid,annoRiep]);

  // NOTA: l'approvazione dal Riepilogo Annuale è stata rimossa — l'unico percorso reale di
  // approvazione ora è la pagina dedicata "Approvazione Note Spese", per evitare due
  // implementazioni parallele che rischiavano di creare movimenti economici duplicati.

  const aggiorna = async (mese, campo, valore) => {
    const nsId = `${orgId}_${uid}_${annoRiep}_${mese}`;
    const ns = noteRiep.find(n => String(n.mese) === String(mese));
    if (ns) {
      await updateDoc(doc(db, "noteSpese", nsId), { [campo]: valore, updatedAt: serverTimestamp() });
    } else {
      await setDoc(doc(db, "noteSpese", nsId), { orgId, uid, anno:annoRiep, mese, stato: "bozza", statoPagamento: "-", righe: {}, [campo]: valore, createdAt: serverTimestamp() });
    }
    addToast("Aggiornato", "success");
  };

  const totaleAnno = noteRiep.reduce((s, ns) => {
    return s + Object.values(ns.righe || {}).reduce((sr, r) => sr + parseN(r.totale), 0);
  }, 0);

  const thStyle = { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#fff", borderRight:"1px solid #4f46e5", whiteSpace:"nowrap" };
  const tdStyle = { padding:"6px 10px", borderBottom:"1px solid #f3f4f6", borderRight:"1px solid #f3f4f6", fontSize:12 };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div style={{ background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"12px 20px", flexShrink:0, display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onBack} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11, fontFamily:"'Inter',sans-serif" }}>← Torna</button>
        <h2 style={{ fontSize:15, fontWeight:700 }}>📊 Riepilogo Annuale</h2>
        <div style={{ display:"flex", alignItems:"center", gap:6 }}>
          <button onClick={()=>setAnnoRiep(a=>a-1)} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:12 }}>‹</button>
          <span style={{ fontSize:15, fontWeight:700, minWidth:50, textAlign:"center" }}>{annoRiep}</span>
          <button onClick={()=>setAnnoRiep(a=>a+1)} style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer", fontSize:12 }}>›</button>
        </div>
      </div>
      <div style={{ flex:1, overflowY:"auto", padding:20 }}>
        <table style={{ borderCollapse:"collapse", width:"100%" }}>
          <thead>
            <tr style={{ background:"#4f46e5" }}>
              <th style={thStyle}>Mese</th>
              <th style={{ ...thStyle, textAlign:"right" }}>Totale</th>
              <th style={thStyle}>Stato nota</th>
              <th style={thStyle}>Stato pagamento</th>
              <th style={thStyle}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {MESI.map((nome, i) => {
              const mese = i + 1;
              const ns = noteRiep.find(n => String(n.mese) === String(mese));
              const totMese = Object.values(ns?.righe || {}).reduce((s, r) => s + parseN(r.totale), 0);
              const stato = ns?.stato || "bozza";
              const statoPag = ns?.statoPagamento || "-";
              const stNs = STATI_NS[stato];

              return (
                <tr key={mese} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td style={{ ...tdStyle, fontWeight:600 }}>{nome} {anno}</td>
                  <td style={{ ...tdStyle, textAlign:"right", fontWeight:700, color: totMese > 0 ? "#059669" : "#9ca3af" }}>
                    {totMese > 0 ? `€ ${fmtTot(totMese)}` : "—"}
                  </td>
                  <td style={tdStyle}>
                    <span style={{ padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:700, background:stNs.bg, color:stNs.colore }}>{stNs.label}</span>
                  </td>
                  <td style={tdStyle}>
                    {canVerify ? (
                      <select value={statoPag} onChange={e => aggiorna(mese, "statoPagamento", e.target.value)}
                        style={{ padding:"3px 8px", border:"1px solid #e5e7eb", borderRadius:12, fontSize:11, fontFamily:"'Inter',sans-serif", fontWeight:600, cursor:"pointer", outline:"none", color: STATI_PAG[statoPag]?.colore || "#6b7280" }}>
                        {Object.entries(STATI_PAG).map(([k,s]) => <option key={k} value={k}>{s.label}</option>)}
                      </select>
                    ) : (
                      <span style={{ fontSize:11, fontWeight:600, color: STATI_PAG[statoPag]?.colore || "#6b7280" }}>{STATI_PAG[statoPag]?.label || "—"}</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {stato==="inviata" && (
                      <span style={{fontSize:10.5,color:"#9ca3af",fontStyle:"italic"}}>Da approvare in Approvazione Note Spese</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background:"#1e1b4b" }}>
              <td style={{ padding:"10px 12px", fontWeight:800, color:"#a5b4fc", fontSize:13 }}>TOTALE ANNO {annoRiep}</td>
              <td style={{ padding:"10px 12px", textAlign:"right", fontWeight:800, color:"#6ee7b7", fontSize:15 }}>€ {fmtTot(totaleAnno)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────
export default function NotaSpesePage({ onNavigate }) {
  const { currentUser, isOrgAdmin, isPlatformOwner, userProfile } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const canVerify = isOrgAdmin || isPlatformOwner;
  // Autorizzare l'invio con presenze mancanti: SOLO titolare e amministrazione (decisione
  // di Paolo, ott 2026) — stessa regola delle Regole Firestore (puoSbloccareNoteSpese).
  const puoSbloccare = isPlatformOwner || userProfile?.role === "titolare" || userProfile?.ruoloOperativo === "amministrazione";
  // L'amministrazione deve poter aprire (in sola lettura) la nota spese altrui per sbloccarla
  const puoVedereAltri = canVerify || puoSbloccare;

  const oggi = new Date();
  const [anno, setAnno] = useState(oggi.getFullYear());
  const [meseSel, setMeseSel] = useState(oggi.getMonth());
  const [utenteSelAdmin, setUtenteSelAdmin] = useState(currentUser?.uid);
  const [utenti, setUtenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [notaMese, setNotaMese] = useState(null);
  const [noteAnno, setNoteAnno] = useState([]);
  const [showTariffa, setShowTariffa] = useState(false);
  const [tariffaMese, setTariffaMese] = useState("");
  const [vistaRiepilogo, setVistaRiepilogo] = useState(false);

  const uid = (puoVedereAltri && utenteSelAdmin) ? utenteSelAdmin : currentUser?.uid;
  const nomeUtente = useMemo(() => {
    if (uid === currentUser?.uid) return currentUser?.displayName || "";
    return utenti.find(u => u.uid === uid)?.displayName || "";
  }, [uid, utenti, currentUser]);

  const nsId = `${effectiveOrgId}_${uid}_${anno}_${meseSel + 1}`;
  const giorni = useMemo(() => giorniDelMese(anno, meseSel), [anno, meseSel]);

  // Navigazione a frecce tra le celle — stesso pattern già usato in Programmazione
  // Settimanale (ID deterministico + focus diretto), adattato al fatto che qui le celle
  // sono "click per modificare", non input sempre presenti: prima le si "sveglia", poi ci
  // si sposta il focus dentro.
  const COLONNE_NAV = ["destinazione","commessaId","km","pedaggi","parcheggi","barRistoranti","varie"];
  const [cellaAttiva, setCellaAttiva] = useState(null); // { giorno, col } | null
  const navigaCella = (giornoAttuale, colAttuale, direzione) => {
    const idx = COLONNE_NAV.indexOf(colAttuale);
    let nuovoGiorno = giornoAttuale, nuovoIdx = idx;
    if (direzione==="up") nuovoGiorno = giornoAttuale - 1;
    if (direzione==="down") nuovoGiorno = giornoAttuale + 1;
    if (direzione==="left") nuovoIdx = idx - 1;
    if (direzione==="right") nuovoIdx = idx + 1;
    if (nuovoGiorno < 1 || nuovoGiorno > giorni.length) return;
    if (nuovoIdx < 0 || nuovoIdx >= COLONNE_NAV.length) return;
    setCellaAttiva({ giorno: nuovoGiorno, col: COLONNE_NAV[nuovoIdx] });
  };


  // Presenze dello stesso periodo — blocco invio se ci sono giorni feriali non compilati
  // (Ferie/Permesso/Malattia contano come compilati: basta che ci sia una timbratura quel giorno)
  const [giorniPresenzePeriodo, setGiorniPresenzePeriodo] = useState({});
  useEffect(() => {
    if (!effectiveOrgId || !uid) return;
    const presId = `${effectiveOrgId}_${uid}_${anno}_${meseSel + 1}`;
    return onSnapshot(doc(db, "presenze", presId), snap => {
      setGiorniPresenzePeriodo(snap.exists() ? (snap.data().giorni || {}) : {});
    });
  }, [effectiveOrgId, uid, anno, meseSel]);

  // Giorni da compilare: regola unica (utils/periodoLavorativo.js), la stessa del
  // cartellino — solo feriali, dentro il periodo di lavoro della persona (assunzione/
  // cessazione), fino a ieri, nessuno per chi è escluso dalle timbrature.
  const periodo = usePeriodoLavorativo(effectiveOrgId, uid);
  const mancantiPresenze = useMemo(() => {
    if (!periodo.pronto) return [];
    return giorniMancanti(giorniAttesiMese(anno, meseSel, periodo), giorniPresenzePeriodo);
  }, [giorniPresenzePeriodo, anno, meseSel, periodo]);

  // Sblocco motivato (titolare/amministrazione): consente l'invio anche con presenze mancanti
  const [sblocco, setSblocco] = useState(null);
  const [chiediMotivoSblocco, setChiediMotivoSblocco] = useState(false);
  const [motivoSblocco, setMotivoSblocco] = useState("");
  useEffect(() => {
    if (!effectiveOrgId || !uid) return;
    return onSnapshot(doc(db, "sblocchiNoteSpese", `${effectiveOrgId}_${uid}_${anno}_${meseSel + 1}`),
      snap => setSblocco(snap.exists() ? snap.data() : null),
      () => setSblocco(null));
  }, [effectiveOrgId, uid, anno, meseSel]);
  const bloccoPresenze = mancantiPresenze.length > 0 && !sblocco;

  const confermaSblocco = async () => {
    const motivo = motivoSblocco.trim();
    if (!motivo) return;
    try {
      await setDoc(doc(db, "sblocchiNoteSpese", `${effectiveOrgId}_${uid}_${anno}_${meseSel + 1}`), {
        organizationId: effectiveOrgId, uid, anno, mese: meseSel + 1, motivo,
        giorniMancanti: mancantiPresenze,
        sbloccatoDa: currentUser.uid, sbloccatoDaNome: currentUser.displayName || "", sbloccatoIl: serverTimestamp(),
      });
      await logModifica({
        orgId: effectiveOrgId, uid: currentUser.uid, nomeUtente: currentUser.displayName || "",
        sezione: "noteSpese", refId: `${uid}_${anno}_${meseSel + 1}`, refNome: `Nota spese ${nomeUtente} — ${MESI[meseSel]} ${anno}`,
        campo: "sbloccoPresenze", campoLabel: "Invio autorizzato con presenze mancanti",
        before: `${mancantiPresenze.length} giorni mancanti`, after: motivo,
      });
      addToast("Invio autorizzato: ora la nota spese può essere inviata", "success");
      setChiediMotivoSblocco(false); setMotivoSblocco("");
    } catch (e) { addToast("Autorizzazione non riuscita: " + (e.message || ""), "error"); }
  };
  const revocaSblocco = async () => {
    if (!await confirm("Revocare l'autorizzazione? La nota spese tornerà bloccata finché mancano presenze.", { type:"warning" })) return;
    try {
      await deleteDoc(doc(db, "sblocchiNoteSpese", `${effectiveOrgId}_${uid}_${anno}_${meseSel + 1}`));
      await logModifica({
        orgId: effectiveOrgId, uid: currentUser.uid, nomeUtente: currentUser.displayName || "",
        sezione: "noteSpese", refId: `${uid}_${anno}_${meseSel + 1}`, refNome: `Nota spese ${nomeUtente} — ${MESI[meseSel]} ${anno}`,
        campo: "sbloccoPresenze", campoLabel: "Autorizzazione all'invio revocata", before: sblocco?.motivo || "", after: "",
      });
      addToast("Autorizzazione revocata", "info");
    } catch (e) { addToast("Revoca non riuscita: " + (e.message || ""), "error"); }
  };
  const isSettCorrente = anno === oggi.getFullYear() && meseSel === oggi.getMonth();
  const canEdit = uid === currentUser?.uid
    ? (!notaMese || notaMese.stato === "bozza")
    : (canVerify && notaMese?.stato !== "liquidata");
  const isAdminVistaAltrui = uid !== currentUser?.uid && canVerify;

  useEffect(() => {
    if (!effectiveOrgId || !puoVedereAltri) return;
    return onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),
      snap => setUtenti(snap.docs.map(d => d.data())));
  }, [effectiveOrgId, puoVedereAltri]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId),where("statoCommessa","==","attiva")),
      snap => setCommesse(snap.docs.map(d => ({id:d.id,...d.data()})).filter(p => !p.archived && !p.isParent)));
  }, [effectiveOrgId]);

  useEffect(() => {
    if (!effectiveOrgId || !uid) return;
    return onSnapshot(doc(db,"noteSpese",nsId), snap => {
      if (snap.exists()) { setNotaMese(snap.data()); setTariffaMese(snap.data().tariffaKm || ""); }
      else { setNotaMese(null); setTariffaMese(""); }
    });
  }, [nsId, effectiveOrgId, uid]);

  useEffect(() => {
    if (!effectiveOrgId || !uid) return;
    const q = query(collection(db,"noteSpese"),where("orgId","==",effectiveOrgId),where("uid","==",uid));
    return onSnapshot(q, snap => setNoteAnno(snap.docs.map(d => d.data()).filter(d=>String(d.anno)===String(anno))));
  }, [effectiveOrgId, uid, anno]);

  const aggiornaCella = useCallback(async (giorno, campo, valore) => {
    if (!canEdit) return;
    const righeCorrente = notaMese?.righe || {};
    const rigaCorrente = righeCorrente[giorno] || {};
    const nuovaRiga = { ...rigaCorrente, [campo]: valore };

    const kmVal = campo === "km" ? parseN(valore) : parseN(rigaCorrente.km);
    const tar = parseN(tariffaMese || notaMese?.tariffaKm || 0);
    nuovaRiga.rimborsoKm = tar > 0 && kmVal > 0 ? (kmVal * tar).toFixed(2) : "";
    nuovaRiga.totale = (
      parseN(nuovaRiga.rimborsoKm) + parseN(nuovaRiga.pedaggi) +
      parseN(nuovaRiga.parcheggi) + parseN(nuovaRiga.barRistoranti) + parseN(nuovaRiga.varie)
    ).toFixed(2);

    const nuoveRighe = { ...righeCorrente, [giorno]: nuovaRiga };
    const base = { orgId:effectiveOrgId, uid, anno, mese:meseSel+1, stato:"bozza", tariffaKm:parseN(tariffaMese), righe:nuoveRighe, updatedAt:serverTimestamp() };
    if (!notaMese) await setDoc(doc(db,"noteSpese",nsId), { ...base, createdAt:serverTimestamp() });
    else await updateDoc(doc(db,"noteSpese",nsId), { righe:nuoveRighe, updatedAt:serverTimestamp() });
  }, [notaMese, nsId, effectiveOrgId, uid, anno, meseSel, tariffaMese, canEdit]);

  const salvaTariffa = async () => {
    const tar = parseN(tariffaMese);
    if (!notaMese) {
      await setDoc(doc(db,"noteSpese",nsId), { orgId:effectiveOrgId, uid, anno, mese:meseSel+1, stato:"bozza", tariffaKm:tar, righe:{}, createdAt:serverTimestamp() });
    } else {
      const nuoveRighe = { ...notaMese.righe };
      Object.keys(nuoveRighe).forEach(g => {
        const r = nuoveRighe[g];
        const rimb = tar > 0 && parseN(r.km) > 0 ? (parseN(r.km) * tar).toFixed(2) : "";
        nuoveRighe[g] = { ...r, rimborsoKm:rimb, totale:(parseN(rimb)+parseN(r.pedaggi)+parseN(r.parcheggi)+parseN(r.barRistoranti)+parseN(r.varie)).toFixed(2) };
      });
      await updateDoc(doc(db,"noteSpese",nsId), { tariffaKm:tar, righe:nuoveRighe, updatedAt:serverTimestamp() });
    }
    setShowTariffa(false);
    addToast("Tariffa salvata e rimborsi ricalcolati ✅","success");
  };

  const inviaNotaSpese = async () => {
    if (bloccoPresenze) {
      addToast(`Prima compila le presenze mancanti (${mancantiPresenze.length} giorni) di questo periodo.`, "error");
      return;
    }
    if (!await confirm("Inviare la nota spese all'amministrazione?", { type:"danger" })) return;
    if (!notaMese) await setDoc(doc(db,"noteSpese",nsId), { orgId:effectiveOrgId, uid, anno, mese:meseSel+1, stato:"inviata", tariffaKm:parseN(tariffaMese), righe:{}, createdAt:serverTimestamp() });
    else await updateDoc(doc(db,"noteSpese",nsId), { stato:"inviata", inviataIl:serverTimestamp() });
    addToast("Nota spese inviata ✅","success");
  };

  // NOTA: qui viveva in passato una seconda implementazione di "aggiornaStato" che
  // approvava la nota spese e creava movimenti economici — non era mai richiamata da
  // nessun bottone in questa pagina (codice morto, verificato). L'unico percorso reale di
  // approvazione resta ApprovazioneNoteSpesePage.js, così l'operazione rimane univoca.

  const stampaPDF = () => window.print();

  const esportaCSV = () => {
    const righe = notaMese?.righe || {};
    const header = "Giorno;Data;Destinazione;Commessa;Km;Rimborso km;Pedaggi;Parcheggi;Bar/Ristoranti;Varie;Totale";
    const rows = giorni.map(({ giorno, dow }) => {
      const r = righe[giorno] || {};
      const comm = commesse.find(c => c.id === r.commessaId)?.name || "";
      return [dow, `${giorno}/${meseSel+1}/${anno}`, r.destinazione||"", comm, r.km||"", r.rimborsoKm||"", r.pedaggi||"", r.parcheggi||"", r.barRistoranti||"", r.varie||"", r.totale||""].join(";");
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type:"text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `NotaSpese_${nomeUtente.replace(" ","_")}_${MESI[meseSel]}_${anno}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const righe = notaMese?.righe || {};
  const tariffa = parseN(tariffaMese || notaMese?.tariffaKm || 0);
  const statoNs = notaMese?.stato || "bozza";
  const stNs = STATI_NS[statoNs];
  // Avviso tariffa km: c'è almeno una riga con km ma tariffa non impostata
  const haKmSenzaTariffa = tariffa <= 0 && Object.values(notaMese?.righe || {}).some(r => parseN(r.km) > 0);

  const totali = useMemo(() => {
    const t = { km:0, rimborsoKm:0, pedaggi:0, parcheggi:0, barRistoranti:0, varie:0, totale:0 };
    Object.values(righe).forEach(r => {
      t.km += parseN(r.km); t.rimborsoKm += parseN(r.rimborsoKm);
      t.pedaggi += parseN(r.pedaggi); t.parcheggi += parseN(r.parcheggi);
      t.barRistoranti += parseN(r.barRistoranti); t.varie += parseN(r.varie);
      t.totale += parseN(r.totale);
    });
    return t;
  }, [righe]);

  if (vistaRiepilogo) return (
    <RiepilogoAnnuale anno={anno} uid={uid} orgId={effectiveOrgId} canVerify={canVerify}
      onBack={() => setVistaRiepilogo(false)} />
  );

  const COL = { dow:40, data:42, dest:130, comm:120, km:55, rimb:80, ped:65, park:65, bar:80, varie:65, tot:88 };
  const tabW = Object.values(COL).reduce((a,b) => a+b, 0);

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>

      {/* Header */}
      <div style={{ background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"10px 20px", flexShrink:0 }}>
        <div style={{ marginBottom:8 }}>
          <h2 style={{ fontSize:15, fontWeight:700, display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
            <FileText size={16} color="#7c3aed"/>
            Nota Spese — <span style={{ color:"#7c3aed" }}>{nomeUtente}</span>
            {puoVedereAltri && (
              <select value={utenteSelAdmin} onChange={e => setUtenteSelAdmin(e.target.value)}
                style={{ padding:"4px 8px", border:"1px solid #e5e7eb", borderRadius:6, fontSize:11, fontFamily:"'Inter',sans-serif", outline:"none", fontWeight:400 }}>
                <option value={currentUser?.uid}>La mia nota spese</option>
                {utenti.filter(u => u.uid !== currentUser?.uid).map(u => <option key={u.uid} value={u.uid}>{u.displayName}</option>)}
              </select>
            )}
            <button onClick={() => setVistaRiepilogo(true)}
              style={{ border:"1px solid #7c3aed", background:"#ede9fe", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11, color:"#7c3aed", fontFamily:"'Inter',sans-serif", fontWeight:600 }}>
              📊 Riepilogo {anno}
            </button>
            <button onClick={() => setShowTariffa(!showTariffa)}
              style={{ border:"1px solid #e5e7eb", background: showTariffa?"#ede9fe":"#fff", borderRadius:6, padding:"4px 10px", cursor:"pointer", fontSize:11, color:"#7c3aed", fontFamily:"'Inter',sans-serif", display:"flex", alignItems:"center", gap:4, fontWeight:600 }}>
              <Settings size={11}/> €/km: {tariffa > 0 ? tariffa.toLocaleString("it-IT",{minimumFractionDigits:2}) : "—"}
            </button>
          </h2>
        </div>

        {/* Banner km senza tariffa */}
        {/* Banner Admin che vede nota altrui */}
        {isAdminVistaAltrui && statoNs === "bozza" && (
          <div style={{ background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, padding:"8px 14px", marginBottom:8, display:"flex", alignItems:"center", gap:8 }}>
            <span>ℹ️</span>
            <span style={{ fontSize:12, color:"#0369a1" }}>
              Stai visualizzando la nota spese di un altro utente in sola lettura.
              Per correggere errori, <strong>rispingi la nota</strong> con una nota esplicativa.
            </span>
          </div>
        )}

        {haKmSenzaTariffa && (
          <div style={{ background:"#fef3c7", border:"1px solid #f59e0b", borderRadius:8, padding:"8px 14px", marginBottom:8, display:"flex", alignItems:"center", gap:8 }}>
            <span>⚠️</span>
            <span style={{ fontSize:12, color:"#92400e" }}>
              Hai inserito km in alcune giornate ma la <strong>tariffa €/km non è impostata</strong> — il rimborso km non verrà calcolato.
              Clicca su <strong>€/km</strong> in alto per impostarla.
            </span>
          </div>
        )}

        {showTariffa && (
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8, padding:"8px 12px", background:"#faf5ff", borderRadius:8, border:"1px solid #e9d5ff" }}>
            <span style={{ fontSize:12, color:"#7c3aed", fontWeight:600 }}>Rimborso €/km per {MESI[meseSel]} {anno}:</span>
            <input type="number" step="0.01" min="0" value={tariffaMese} onChange={e => setTariffaMese(e.target.value)} placeholder="es. 0.30"
              style={{ width:100, padding:"4px 8px", border:"1px solid #7c3aed", borderRadius:6, fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none" }}/>
            <button onClick={salvaTariffa} className="btn btn-primary" style={{ fontSize:11, padding:"4px 12px" }}>Salva</button>
            <span style={{ fontSize:10, color:"#9ca3af" }}>Tutti i rimborsi km del mese vengono ricalcolati</span>
          </div>
        )}

        {/* Nav mese + azioni */}
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <button onClick={() => { if(meseSel===0){setMeseSel(11);setAnno(a=>a-1);}else setMeseSel(m=>m-1); }}
            style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer", display:"flex", alignItems:"center" }}>
            <ChevronLeft size={14}/>
          </button>
          <span style={{ fontSize:14, fontWeight:700, minWidth:150, textAlign:"center" }}>{MESI[meseSel]} {anno}</span>
          <button onClick={() => { if(meseSel===11){setMeseSel(0);setAnno(a=>a+1);}else setMeseSel(m=>m+1); }}
            style={{ border:"1px solid #e5e7eb", background:"#fff", borderRadius:6, padding:"3px 8px", cursor:"pointer", display:"flex", alignItems:"center" }}>
            <ChevronRight size={14}/>
          </button>
          <span style={{ padding:"2px 10px", borderRadius:12, fontSize:11, fontWeight:700, background:stNs.bg, color:stNs.colore }}>{stNs.label}</span>

          {statoNs === "bozza" && uid === currentUser?.uid && (
            <button onClick={inviaNotaSpese} disabled={bloccoPresenze}
              title={bloccoPresenze ? `Presenze mancanti in questo periodo: ${mancantiPresenze.length} giorni` : ""}
              style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 12px", border:`1px solid ${bloccoPresenze?"#d1d5db":"#2563eb"}`, borderRadius:7, background:bloccoPresenze?"#f3f4f6":"#dbeafe", color:bloccoPresenze?"#9ca3af":"#2563eb", cursor: bloccoPresenze?"not-allowed":"pointer", fontSize:11, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>
              <Send size={12}/> Invia
            </button>
          )}
          <button onClick={esportaCSV}
            style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 12px", border:"1px solid #059669", borderRadius:7, background:"#d1fae5", color:"#059669", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>
            <Download size={12}/> Esporta
          </button>
          <button onClick={stampaPDF}
            style={{ display:"flex", alignItems:"center", gap:4, padding:"5px 12px", border:"1px solid #6b7280", borderRadius:7, background:"#f3f4f6", color:"#374151", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"'Inter',sans-serif" }}>
            <Printer size={12}/> Stampa PDF
          </button>
        </div>
      </div>

      {statoNs === "bozza" && uid === currentUser?.uid && bloccoPresenze && (
        <div style={{ background:"#fef3c7", borderBottom:"1px solid #fde68a", padding:"8px 20px", flexShrink:0, fontSize:11, color:"#92400e" }}>
          ⚠️ Non puoi ancora inviare la nota spese: mancano le presenze di {mancantiPresenze.length} {mancantiPresenze.length===1?"giorno":"giorni"} di questo periodo ({mancantiPresenze.join(", ")}). Vai su Presenze e completali, oppure registra un'assenza (malattia, ferie, permesso).
        </div>
      )}
      {statoNs === "bozza" && sblocco && mancantiPresenze.length > 0 && (
        <div style={{ background:"#eff6ff", borderBottom:"1px solid #bfdbfe", padding:"8px 20px", flexShrink:0, fontSize:11, color:"#1e40af", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ flex:1 }}>🔓 Invio autorizzato da {sblocco.sbloccatoDaNome || "amministrazione"}{sblocco.sbloccatoIl?.toDate ? ` il ${sblocco.sbloccatoIl.toDate().toLocaleDateString("it-IT")}` : ""} nonostante {mancantiPresenze.length} {mancantiPresenze.length===1?"giorno":"giorni"} senza presenze. Motivo: {sblocco.motivo}</span>
          {puoSbloccare && <button onClick={revocaSblocco} style={{ fontSize:11, border:"1px solid #93c5fd", background:"#fff", borderRadius:6, padding:"2px 10px", cursor:"pointer", color:"#1e40af" }}>Revoca autorizzazione</button>}
        </div>
      )}
      {statoNs === "bozza" && uid !== currentUser?.uid && puoSbloccare && bloccoPresenze && (
        <div style={{ background:"#fef3c7", borderBottom:"1px solid #fde68a", padding:"8px 20px", flexShrink:0, fontSize:11, color:"#92400e", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <span style={{ flex:1 }}>⚠️ {nomeUtente} non può inviare questa nota spese: mancano le presenze di {mancantiPresenze.length} {mancantiPresenze.length===1?"giorno":"giorni"} ({mancantiPresenze.join(", ")}).</span>
          <button onClick={()=>setChiediMotivoSblocco(true)} style={{ fontSize:11, fontWeight:700, border:"1px solid #d97706", background:"#fff", borderRadius:6, padding:"3px 10px", cursor:"pointer", color:"#92400e" }}>🔓 Autorizza l'invio</button>
        </div>
      )}
      {chiediMotivoSblocco && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setChiediMotivoSblocco(false)}>
          <div className="modal" style={{ maxWidth:420 }}>
            <h3 style={{ fontSize:14, fontWeight:700, marginBottom:8 }}>Autorizza l'invio senza presenze complete</h3>
            <p style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>{nomeUtente} potrà inviare la nota spese di {MESI[meseSel]} {anno} anche se mancano {mancantiPresenze.length} {mancantiPresenze.length===1?"giorno":"giorni"} di presenze. Il motivo resta nel registro modifiche.</p>
            <textarea className="input" value={motivoSblocco} onChange={e=>setMotivoSblocco(e.target.value)} placeholder="Es. assunto a metà mese, certificato di malattia già ricevuto…" style={{ minHeight:70, resize:"vertical", width:"100%" }}/>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:10 }}>
              <button className="btn" onClick={()=>setChiediMotivoSblocco(false)}>Annulla</button>
              <button className="btn btn-primary" disabled={!motivoSblocco.trim()} onClick={confermaSblocco}>Autorizza</button>
            </div>
          </div>
        </div>
      )}

      {/* Banner stato non modificabile */}
      {statoNs !== "bozza" && (
        <div style={{ background: statoNs==="approvata"||statoNs==="liquidata" ? "#d1fae5" : "#dbeafe",
          borderBottom:`1px solid ${statoNs==="approvata"||statoNs==="liquidata"?"#6ee7b7":"#93c5fd"}`,
          padding:"8px 20px", flexShrink:0, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:12, fontWeight:600, color: statoNs==="approvata"||statoNs==="liquidata" ? "#065f46" : "#1e40af" }}>
            {statoNs==="inviata" && "📤 Nota spese inviata all'amministrazione — in attesa di approvazione. Non è possibile modificare i dati."}
            {statoNs==="in_verifica" && "🔍 Nota spese in verifica — non è possibile modificare i dati."}
            {statoNs==="approvata" && "✅ Nota spese approvata — i dati sono bloccati. Contatta l'amministrazione per richiedere eventuali modifiche."}
            {statoNs==="liquidata" && "💰 Nota spese liquidata — operazione conclusa."}
            {statoNs==="respinta" && "❌ Nota spese respinta — contatta l'amministrazione per chiarimenti."}
          </span>
        </div>
      )}

      {/* Griglia */}
      <div style={{ flex:1, overflowY:"auto", overflowX:"auto" }}>
        <table style={{ borderCollapse:"collapse", tableLayout:"fixed", width:tabW, minWidth:tabW, fontSize:12 }}>
          <colgroup>
            <col style={{ width:COL.dow }}/><col style={{ width:COL.data }}/><col style={{ width:COL.dest }}/>
            <col style={{ width:COL.comm }}/><col style={{ width:COL.km }}/><col style={{ width:COL.rimb }}/>
            <col style={{ width:COL.ped }}/><col style={{ width:COL.park }}/><col style={{ width:COL.bar }}/>
            <col style={{ width:COL.varie }}/><col style={{ width:COL.tot }}/>
          </colgroup>
          <thead style={{ position:"sticky", top:0, zIndex:5 }}>
            <tr style={{ background:"#5b21b6" }}>
              {["Giorno","Data","Destinazione","Commessa","Km","Rimborso km","Pedaggi","Parcheggi","Bar/Ristoranti","Varie","Totale"].map((h,i) => (
                <th key={i} style={{ padding:"7px 6px", textAlign: i>=4?"right":"left", fontSize:10, fontWeight:700, color:"#fff", borderRight:"1px solid #7c3aed", whiteSpace:"nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {giorni.map(({ giorno, dow, isSabDom }) => {
              const riga = righe[giorno] || {};
              const isOggiRow = isSettCorrente && giorno === oggi.getDate();
              const rowBg = isOggiRow ? "#ede9fe" : isSabDom ? "#faf5ff" : giorno%2===0 ? "#fff" : "#fafafa";
              return (
                <tr key={giorno} style={{ background:rowBg, borderBottom:"1px solid #f3f4f6" }}>
                  <td style={{ padding:"1px 6px", fontWeight:700, fontSize:11, color:isSabDom?"#7c3aed":"#6b7280", borderRight:"1px solid #f3f4f6" }}>{dow}</td>
                  <td style={{ padding:"1px 6px", fontWeight:isOggiRow?700:400, color:isOggiRow?"#7c3aed":"#374151", borderRight:"1px solid #f3f4f6", whiteSpace:"nowrap" }}>{giorno}/{meseSel+1}</td>
                  <td style={{ borderRight:"1px solid #f3f4f6", maxWidth:COL.dest }}>
                    <CellaTesto value={riga.destinazione} onChange={v=>aggiornaCella(giorno,"destinazione",v)} disabled={!canEdit} placeholder="Destinazione..."
                      id={`ns-${giorno}-destinazione`} attiva={cellaAttiva?.giorno===giorno&&cellaAttiva?.col==="destinazione"} onNav={d=>navigaCella(giorno,"destinazione",d)}/>
                  </td>
                  <td style={{ borderRight:"1px solid #f3f4f6" }}>
                    <CellaCommessa value={riga.commessaId} onChange={v=>aggiornaCella(giorno,"commessaId",v)} commesse={commesse} disabled={!canEdit}
                      id={`ns-${giorno}-commessaId`} attiva={cellaAttiva?.giorno===giorno&&cellaAttiva?.col==="commessaId"} onNav={d=>navigaCella(giorno,"commessaId",d)}/>
                  </td>
                  <td style={{ borderRight:"1px solid #f3f4f6" }}>
                    <CellaNum value={riga.km} onChange={v=>aggiornaCella(giorno,"km",v)} disabled={!canEdit}
                      id={`ns-${giorno}-km`} attiva={cellaAttiva?.giorno===giorno&&cellaAttiva?.col==="km"} onNav={d=>navigaCella(giorno,"km",d)}/>
                  </td>
                  <td style={{ borderRight:"1px solid #f3f4f6", background: parseN(riga.rimborsoKm)>0?"#f0fdf4":"transparent" }}>
                    <div style={{ padding:"3px 6px", fontSize:12, textAlign:"right", color:"#059669", fontWeight: parseN(riga.rimborsoKm)>0?700:400 }}>
                      {parseN(riga.rimborsoKm)>0?`€ ${fmt(riga.rimborsoKm)}`:"—"}
                    </div>
                  </td>
                  <td style={{ borderRight:"1px solid #f3f4f6" }}><CellaNum value={riga.pedaggi} onChange={v=>aggiornaCella(giorno,"pedaggi",v)} disabled={!canEdit}
                    id={`ns-${giorno}-pedaggi`} attiva={cellaAttiva?.giorno===giorno&&cellaAttiva?.col==="pedaggi"} onNav={d=>navigaCella(giorno,"pedaggi",d)}/></td>
                  <td style={{ borderRight:"1px solid #f3f4f6" }}><CellaNum value={riga.parcheggi} onChange={v=>aggiornaCella(giorno,"parcheggi",v)} disabled={!canEdit}
                    id={`ns-${giorno}-parcheggi`} attiva={cellaAttiva?.giorno===giorno&&cellaAttiva?.col==="parcheggi"} onNav={d=>navigaCella(giorno,"parcheggi",d)}/></td>
                  <td style={{ borderRight:"1px solid #f3f4f6" }}><CellaNum value={riga.barRistoranti} onChange={v=>aggiornaCella(giorno,"barRistoranti",v)} disabled={!canEdit}
                    id={`ns-${giorno}-barRistoranti`} attiva={cellaAttiva?.giorno===giorno&&cellaAttiva?.col==="barRistoranti"} onNav={d=>navigaCella(giorno,"barRistoranti",d)}/></td>
                  <td style={{ borderRight:"1px solid #f3f4f6" }}><CellaNum value={riga.varie} onChange={v=>aggiornaCella(giorno,"varie",v)} disabled={!canEdit}
                    id={`ns-${giorno}-varie`} attiva={cellaAttiva?.giorno===giorno&&cellaAttiva?.col==="varie"} onNav={d=>navigaCella(giorno,"varie",d)}/></td>
                  <td style={{ background: parseN(riga.totale)>0?"#f0fdf4":"transparent" }}>
                    <div style={{ padding:"3px 8px", fontSize:12, textAlign:"right", fontWeight:700, color: parseN(riga.totale)>0?"#059669":"#d1d5db" }}>
                      {parseN(riga.totale)>0?`€ ${fmt(riga.totale)}`:"—"}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ background:"#1e1b4b", position:"sticky", bottom:0 }}>
              <td colSpan={2} style={{ padding:"8px 10px", fontWeight:700, fontSize:11, color:"#a5b4fc" }}>TOTALI MESE</td>
              <td colSpan={2} style={{ borderRight:"1px solid #3730a3" }}/>
              <td style={{ padding:"6px 6px", textAlign:"right", fontWeight:700, color:"#e0e7ff", fontSize:12, borderRight:"1px solid #3730a3" }}>{totali.km>0?`${totali.km} km`:"—"}</td>
              <td style={{ padding:"6px 6px", textAlign:"right", fontWeight:700, color:"#6ee7b7", fontSize:12, borderRight:"1px solid #3730a3" }}>{totali.rimborsoKm>0?`€ ${fmtTot(totali.rimborsoKm)}`:"—"}</td>
              {[totali.pedaggi,totali.parcheggi,totali.barRistoranti,totali.varie].map((v,i)=>(
                <td key={i} style={{ padding:"6px 6px", textAlign:"right", fontWeight:700, color:"#e0e7ff", fontSize:12, borderRight:"1px solid #3730a3" }}>{v>0?`€ ${fmtTot(v)}`:"—"}</td>
              ))}
              <td style={{ padding:"6px 8px", textAlign:"right", fontWeight:800, color:"#6ee7b7", fontSize:14 }}>€ {fmtTot(totali.totale)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
